import React, { useState, useMemo, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Mail,
  Home, 
  LogOut, 
  Menu, 
  X,
  Users,
  User,
  Trophy,
  Sun,
  Moon,
  BarChart3,
  Target,
  ChevronRight,
  Settings,
  MapPin,
  BookOpen,
  TrendingUp,
  Calendar,
  Video,
  DollarSign,
  Database,
  FileText,
  Star,
  MessageSquare,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';

// Memoized nav items to prevent re-creation
const PLAYER_NAV_ITEMS = [
  { path: '/dashboard', label: 'Home', icon: Home },
  { path: '/shots', label: 'Log Shots', icon: Target },
  { path: '/games', label: 'Log Games', icon: Trophy },
  { path: '/training', label: 'Training', icon: BookOpen },
  { path: '/heatmap', label: 'Heatmap', icon: MapPin },
  { path: '/share-stats', label: 'Share', icon: Users },
  { path: '/profile', label: 'Profile', icon: User },
];

const COACH_NAV_ITEMS = [
  { path: '/coach', label: 'Home', icon: Home },
  { path: '/coach/assist', label: 'Log Shots', icon: Target },
  { path: '/coach/my-players', label: 'Roster', icon: Users },
  { path: '/training', label: 'Training', icon: BookOpen },
  { path: '/rankings', label: 'Teams', icon: Trophy },
  { path: '/profile', label: 'Settings', icon: Settings },
];

const LayoutComponent = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isCoach = user?.role === 'coach';
  const isAdmin = user?.is_admin;
  
  // Use memoized nav items
  const navItems = useMemo(() => isCoach ? COACH_NAV_ITEMS : PLAYER_NAV_ITEMS, [isCoach]);
  
  // Admin gets analytics + roster + settings
  // Admin nav items (defined inline as it's rarely used)
  const adminNavItems = useMemo(() => [
    { path: '/coach', label: 'Home', icon: Home },
    { path: '/admin/players', label: 'Players', icon: Users },
    { path: '/admin/hall-of-fame', label: 'Hall of Fame', icon: Video },
    { path: '/admin/signups', label: 'Sign-Ups', icon: Mail },
    { path: '/admin/worksheets', label: 'Worksheets', icon: FileText },
    { path: '/admin/training', label: 'Training', icon: BookOpen },
    { path: '/admin/cms', label: 'Content', icon: FileText },
    { path: '/admin/revenue', label: 'Revenue', icon: DollarSign },
    { path: '/admin/court-iq', label: 'Court IQ', icon: Database },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/profile', label: 'Settings', icon: Settings },
  ], []);
  
  const currentNavItems = useMemo(() => 
    isAdmin ? adminNavItems : navItems, 
    [isAdmin, adminNavItems, navItems]
  );
  
  const handleLogout = () => {
    logout();
    navigate('/auth');
  };
  
  const isActive = (path) => {
    if (path === '/dashboard' || path === '/coach') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 flex-col bg-card border-r border-border z-40">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <Link to={isCoach ? '/coach' : '/dashboard'} className="flex flex-col">
            <span className="font-heading text-base font-bold tracking-tight text-foreground uppercase">Chris Jordan's</span>
            <span className="font-heading text-xs font-medium tracking-wider text-muted-foreground uppercase">Skill Lab</span>
          </Link>
        </div>
        
        {/* User Info */}
        <div className="px-6 py-4 border-b border-border">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
            {isCoach ? (isAdmin ? 'Admin' : 'Coach') : 'Player'}
          </p>
          <p className="font-semibold text-foreground truncate">{user?.name}</p>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {currentNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                  active 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
                {active && <ChevronRight size={16} className="ml-auto" />}
              </Link>
            );
          })}
        </nav>
        
        {/* Bottom Section */}
        <div className="p-4 border-t border-border space-y-2">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          
          {/* Logout */}
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut size={20} />
            <span>Log Out</span>
          </button>
        </div>
      </aside>
      
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-card/95 backdrop-blur-lg border-b border-border z-50 px-3 flex items-center justify-between safe-area-top">
        <Link to={isCoach ? '/coach' : '/dashboard'} className="flex flex-col">
          <span className="font-heading text-sm font-bold text-foreground uppercase">Chris Jordan's</span>
          <span className="font-heading text-xs text-muted-foreground uppercase">Skill Lab</span>
        </Link>
        
        <div className="flex items-center gap-1">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground"
            data-testid="mobile-theme-toggle"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-background/98 backdrop-blur-lg z-40 pt-20">
          <nav className="p-4 space-y-2">
            {currentNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center justify-between px-5 py-4 rounded-xl font-medium text-lg ${
                    active 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-card text-foreground border border-border'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <Icon size={24} />
                    <span>{item.label}</span>
                  </div>
                  <ChevronRight size={20} className="opacity-50" />
                </Link>
              );
            })}
            
            <div className="pt-4 border-t border-border mt-4">
              <button
                onClick={handleLogout}
                className="flex items-center gap-4 w-full px-5 py-4 rounded-xl bg-destructive/10 text-destructive font-medium text-lg"
              >
                <LogOut size={24} />
                <span>Log Out</span>
              </button>
            </div>
          </nav>
        </div>
      )}
      
      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-lg border-t border-border z-50 safe-area-bottom">
        <div className="flex justify-around items-center h-14">
          {currentNavItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 min-w-[56px] ${
                  active ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                <Icon size={18} strokeWidth={active ? 2.5 : 2} />
                <span className="text-[10px] font-medium leading-tight">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
      
      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-14 md:pt-0 pb-16 md:pb-0">
        <div className="p-3 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

// Export both named and default
export const Layout = memo(LayoutComponent);
export default Layout;
